// Brand API functions
import { getUserInfo } from './auth';
import { CommonUtils } from './commonUtils';
import { getApiV1Url } from '../config/api';

// ============================================================================
// INTERFACES
// ============================================================================

// 브랜드 노드 (API 응답)
export interface BrandNode {
  code: string;
  status: string;
  parentCode?: string;
  depth: number;
  sortOrder?: number;
  name: string;
  logo?: string;
  description?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  subBrands?: BrandNode[];
  shopCount?: number; // includeShopCount=true일 때 포함 (브랜드 소속 매장 수)
}

// 브랜드 목록 응답
export interface BrandListResponse {
  totalCount: number;
  projectCode: string;
  brands: BrandNode[];
}

// 평탄화된 브랜드 노드 (selectbox 등에서 사용)
export interface FlatBrandNode {
  code: string;
  status: string;
  parentCode?: string;
  depth: number;
  sortOrder?: number;
  name: string;
  logo?: string;
  description?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  // subBrands는 없음 (평탄화되었으므로)
}

// 평탄화된 브랜드 목록 응답
export interface FlatBrandListResponse {
  totalCount: number;
  projectCode: string;
  brands: FlatBrandNode[];
}

// 브랜드 생성 요청
export interface BrandCreateRequest {
  code: string;
  status: string;
  parentCode?: string;
  depth: number;
  sortOrder: number;
  name: string;
  logo?: string;
  description?: string;
}

// 브랜드 수정 요청
export interface BrandUpdateRequest {
  code: string;
  status: string;
  sortOrder?: number;
  name: string;
  logo?: string;
  description?: string;
}

// 브랜드 수정 응답
export interface BrandUpdateResponse {
  success: boolean;
  message: string;
  data?: BrandNode;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

// 브랜드 목록 조회 API
export const getBrandList = async (
  projectCode: string,
  includeShopCount?: boolean,
  signal?: AbortSignal
): Promise<BrandListResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const queryParams = new URLSearchParams();
    if (includeShopCount) {
      queryParams.append('includeShopCount', 'true');
    }

    const url = queryParams.toString() 
      ? `${getApiV1Url(`/projects/${projectCode}/brands`)}?${queryParams}`
      : getApiV1Url(`/projects/${projectCode}/brands`);

    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(response.status, errorData);
      throw new Error(errorMessage || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('브랜드 목록 API 응답:', result);
    
    if (result.data) {
      return {
        totalCount: result.data.totalCount || 0,
        projectCode: result.data.projectCode || projectCode,
        brands: result.data.brands || []
      };
    }
    
    return {
      totalCount: 0,
      projectCode: projectCode,
      brands: []
    };
  } catch (error: any) {
    // 요청 취소(AbortController)는 정상 동작이므로 로그/변환하지 않고 그대로 전달
    if (error?.name === 'AbortError') {
      throw error;
    }
    console.error('브랜드 목록 조회 중 오류 발생:', error);

    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }

    throw error;
  }
};

// 평탄화된 브랜드 목록 조회 API
export const getBrandListFlat = async (
  projectCode: string
): Promise<FlatBrandListResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/brands/flat`),
      {
        method: 'GET',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(response.status, errorData);
      throw new Error(errorMessage || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('평탄화된 브랜드 목록 API 응답:', result);
    
    if (result.data) {
      return {
        totalCount: result.data.totalCount || 0,
        projectCode: result.data.projectCode || projectCode,
        brands: result.data.brands || []
      };
    }
    
    return {
      totalCount: 0,
      projectCode: projectCode,
      brands: []
    };
  } catch (error: any) {
    console.error('평탄화된 브랜드 목록 조회 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 브랜드 생성 API
export const createBrand = async (
  projectCode: string,
  brandData: BrandCreateRequest
): Promise<BrandNode> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/brands`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(brandData)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(response.status, errorData);
      throw new Error(errorMessage || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('브랜드 생성 API 응답:', result);
    
    if (result.data) {
      return result.data;
    }
    
    throw new Error('브랜드 생성에 실패했습니다.');
  } catch (error: any) {
    console.error('브랜드 생성 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 브랜드 수정 요청
export interface BrandUpdateRequest {
  code: string;
  status: string;
  name: string;
  sortOrder?: number;
  logo?: string;
  description?: string;
}

// 브랜드 수정 응답
export interface BrandUpdateResponse {
  success: boolean;
  message: string;
  data?: BrandNode;
}

// 브랜드 상세 조회 API
export const getBrandDetail = async (
  projectCode: string,
  brandCode: string
): Promise<BrandNode> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/brands/${brandCode}`),
      {
        method: 'GET',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(response.status, errorData);
      throw new Error(errorMessage || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('브랜드 상세 API 응답:', result);
    
    if (result.data) {
      return result.data;
    }
    
    throw new Error('브랜드 정보를 찾을 수 없습니다.');
  } catch (error: any) {
    console.error('브랜드 상세 조회 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 브랜드 수정 API
export const updateBrand = async (
  projectCode: string,
  brandCode: string,
  brandData: BrandUpdateRequest
): Promise<BrandUpdateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/brands/${brandCode}`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(brandData)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(response.status, errorData);
      throw new Error(errorMessage || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('브랜드 수정 API 응답:', result);
    
    return {
      success: true,
      message: result.message || '브랜드가 성공적으로 수정되었습니다.',
      data: result.data
    };
  } catch (error: any) {
    console.error('브랜드 수정 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

